import React, { useState, useEffect, useRef } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonInput,
  IonButton,
} from '@ionic/react';

import './TextModal.css';
import { ExerciseTrackingMode, trackingLabel } from '../../../../utils/ExerciseTracking';

interface TextModalProps {
  isOpen: boolean;
  onConfirm: (reps: number, weight: number) => void;
  onCancel: () => void;
  trackingMode?: ExerciseTrackingMode;
}

const TextModal: React.FC<TextModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  trackingMode = 'weight',
}) => {
  const [reps, setReps] = useState<string>('');
  const [weight, setWeight] = useState<string>('');
  const repsInputRef = useRef<HTMLIonInputElement>(null);
  const weightInputRef = useRef<HTMLIonInputElement>(null);
  const confirmButtonRef = useRef<HTMLIonButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      setReps('');
      setWeight('');
    }
  }, [isOpen]);

  // only used to enable/disable Confirm
  const isRepsValid = () => /^[1-9]\d*$/.test(reps);
  const isWeightValid = () => {
    if (trackingMode === 'bodyweight' && !weight.trim()) return true;
    const norm = weight.replace(',', '.');
    return /^\d+(\.\d+)?$/.test(norm) && parseFloat(norm) > 0;
  };

  const handleConfirm = () => {
    if (!isRepsValid() || !isWeightValid()) return;
    onConfirm(parseInt(reps, 10), weight.trim() ? parseFloat(weight.replace(',', '.')) : 0);
    setReps('');
    setWeight('');
  };

  const focusWeight = () => {
    if (!isRepsValid()) return;
    void weightInputRef.current?.setFocus();
  };

  const focusConfirm = () => {
    if (!isRepsValid() || !isWeightValid()) return;
    confirmButtonRef.current?.shadowRoot?.querySelector('button')?.focus();
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onCancel}
      onDidPresent={() => void repsInputRef.current?.setFocus()}
      className="set-entry-modal"
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>{trackingMode === 'bodyweight' ? 'Enter Reps' : `Enter Reps and ${trackingLabel(trackingMode)}`}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="set-entry-content">
        <div className="set-entry-form">
          <p className="set-entry-form__intro">
            Add the result of this set. Press Enter to move forward.
          </p>

          <IonInput
            ref={repsInputRef}
            className="set-entry-input"
            label="Reps"
            labelPlacement="stacked"
            fill="outline"
            placeholder="e.g. 10"
            value={reps}
            type="number"
            inputMode="numeric"
            min="1"
            enterkeyhint="next"
            onIonInput={e => setReps(e.detail.value ?? '')}
            onKeyDown={e => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              focusWeight();
            }}
          />

          <IonInput
            ref={weightInputRef}
            className="set-entry-input"
            label={`${trackingLabel(trackingMode)} (kg)${trackingMode === 'bodyweight' ? ' · optional' : ''}`}
            labelPlacement="stacked"
            fill="outline"
            placeholder={trackingMode === 'bodyweight' ? 'Bodyweight' : 'e.g. 25'}
            value={weight}
            inputMode="decimal"
            enterkeyhint="done"
            onIonInput={e => setWeight(e.detail.value ?? '')}
            onKeyDown={e => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              focusConfirm();
            }}
          />

          <div className="set-entry-actions">
            <IonButton
              expand="block"
              fill="outline"
              color="medium"
              onClick={onCancel}
            >
              Cancel
            </IonButton>

            <IonButton
              ref={confirmButtonRef}
              expand="block"
              onClick={handleConfirm}
              disabled={!isRepsValid() || !isWeightValid()}
            >
              Confirm
            </IonButton>
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default TextModal;
